import styled from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../components/AppShell/styles";

export const Intro = styled.p`
  margin-top: 0.25rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-neutral-150);
`;

export const List = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;
  margin-bottom: 2rem;
`;

/** One release per frame, as the domains: radius 16, no border. */
export const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
  font-family: Inter, sans-serif;
  background-color: var(--color-neutral-dark);
  border-radius: 1rem;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    padding: 1.5rem 1.75rem;
  }

  header {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.75rem;
    color: var(--color-neutral-white);

    @media (min-width: ${DESKTOP_BREAKPOINT}) {
      font-size: 1.5rem;
      line-height: 2rem;
    }
  }
`;

const KIND_COLORS = {
  release: "background: var(--color-lemon); color: var(--color-neutral-darkness);",
  improvement: "background: var(--color-brand-blue); color: var(--color-neutral-white);",
  fix: "background: var(--color-neutral-base); color: var(--color-neutral-white);",
};

export const Kind = styled.span<{ $kind: keyof typeof KIND_COLORS }>`
  padding: 2px 10px;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  ${({ $kind }) => KIND_COLORS[$kind]}
`;

export const Media = styled.div`
  overflow: hidden;
  background: var(--color-neutral-darkness);
  border-radius: 12px;

  img,
  video {
    display: block;
    width: 100%;
    height: auto;
  }
`;

/** The text from the Operations (only text markup), in the editor's colors. */
export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-size: 0.9375rem;
  line-height: 1.5rem;
  /* light enough to read on the card; the bold parts stand out in full white */
  color: rgba(255, 255, 255, 0.8);

  h2,
  h3,
  h4,
  strong {
    font-weight: 700;
    color: var(--color-neutral-white);
  }

  h2 {
    font-size: 1.125rem;
  }

  h3 {
    font-size: 1rem;
  }

  ul,
  ol {
    padding-left: 1.5rem;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  li + li {
    margin-top: 0.25rem;
  }

  a {
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  a:hover {
    color: var(--color-neutral-white);
  }

  blockquote {
    padding-left: 1rem;
    border-left: 3px solid var(--color-neutral-base);
  }
`;

export const Cta = styled.a`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 20px;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-neutral-darkness);
  background: var(--color-lemon);
  border-radius: 999px;
`;

export const Reactions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid var(--color-neutral-base);

  .question,
  .status {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-150);
  }

  .status {
    color: var(--color-neutral-white);
  }

  .faces {
    display: flex;
    gap: 0.5rem;
  }
`;

export const Face = styled.button`
  display: grid;
  place-content: center;
  width: 44px;
  height: 44px;
  font-size: 1.375rem;
  line-height: 1;
  background: var(--color-neutral-darkness);
  border: 1px solid var(--color-neutral-base);
  border-radius: 999px;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-neutral-150);
  }

  &[aria-pressed="true"] {
    background: var(--color-neutral-base);
    border-color: var(--color-lemon);
  }

  &:disabled {
    cursor: default;
  }
`;

export const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  color: var(--color-neutral-150);

  button {
    font-weight: 700;
    color: var(--color-neutral-white);
    text-decoration: underline;
  }
`;

export const LoadingMore = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 2rem;
`;

export const LoadingScreen = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .title {
    width: 200px;
    height: 36px;
  }

  .card {
    height: 320px;
    border-radius: 1rem;
  }
`;
